import { PercentCrop } from 'react-image-crop'

import { MAX_DIMENSION } from './imageUtils'

const FULL_CROP_THRESHOLD = 99.5

/**
 * Returns true if the crop covers effectively the entire image,
 * meaning no cropping is needed.
 */
export const isFullImageCrop = (crop: PercentCrop): boolean =>
  crop.x <= 0.5 &&
  crop.y <= 0.5 &&
  crop.width >= FULL_CROP_THRESHOLD &&
  crop.height >= FULL_CROP_THRESHOLD

/**
 * Crops an image file using percentage-based crop coordinates, then
 * resizes so the longest side equals MAX_DIMENSION (3500px).
 * Returns a PNG File.
 */
export const cropAndResizeImage = async (
  file: File,
  crop: PercentCrop
): Promise<File> => {
  const objectUrl = URL.createObjectURL(file)
  const img = new Image()

  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('Failed to load image for cropping'))
      img.src = objectUrl
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
    img.onload = null
    img.onerror = null
  }

  // Convert percentage crop to pixel values
  const srcX = (crop.x / 100) * img.naturalWidth
  const srcY = (crop.y / 100) * img.naturalHeight
  const srcW = (crop.width / 100) * img.naturalWidth
  const srcH = (crop.height / 100) * img.naturalHeight

  if (srcW <= 0 || srcH <= 0) {
    throw new Error('Crop region has invalid dimensions')
  }

  // Scale so longest side of cropped region = MAX_DIMENSION
  const largestDimension = Math.max(srcW, srcH)
  const scale = MAX_DIMENSION / largestDimension
  const outW = Math.round(srcW * scale)
  const outH = Math.round(srcH * scale)

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to get canvas 2D context')
  }

  ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH)

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to convert canvas to blob'))
        }
      },
      'image/png',
      1.0
    )
  })

  const baseName = file.name.replace(/\.[^.]+$/, '')
  return new File([blob], `${baseName}.png`, { type: 'image/png' })
}
